import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import API from "../../services/request";
import { API_PATH } from "../../utils/config";
import { toast } from "react-toastify";
import UserImage from "../UserImage";
import { selectIsUserProfile } from "../../redux/reducers/gallery";
import { openNonAuthenticatedModal } from "../../redux/reducers/nonAuthenticatedModal";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { isUserLoggedIn, getLoggedInUser } from "../../utils/helpers";

const PAGE_SIZE = 10;

const FollowersFollowingModal = ({
  open,
  onClose,
  userId,
  type = "followers",
  onCountsRefresh,
}) => {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isFollowingMap, setIsFollowingMap] = useState({});
  const [rowLoading, setRowLoading] = useState({});
  const [loadingMore, setLoadingMore] = useState(false);
  const scrollRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isUserProfile = useSelector(selectIsUserProfile);

  const title = useMemo(
    () => (type === "following" ? "Following" : "Followers"),
    [type]
  );

  const fetchList = async (newPage = 1, append = false) => {
    if (!userId) return;
    append ? setLoadingMore(true) : setLoading(true);
    try {
      const url =
        type === "following"
          ? API_PATH.GET_USER_FOLLOWING(userId)
          : API_PATH.GET_USER_FOLLOWERS(userId);

      const res = await API.get(url, {
        params: {
          page: newPage,
          page_size: PAGE_SIZE,
          ...(query ? { search: query } : {}),
        },
      });

      const data = res?.data || {};
      const results = Array.isArray(data.results) ? data.results : [];
      setList((prev) => (append ? [...prev, ...results] : results));
      const curr = data?.current_page ?? newPage;
      const total = data?.total_pages ?? newPage;
      setHasMore(Boolean(data?.next) || curr < total);
      setPage(newPage);
      // set initial following map using is_following from API results
      setIsFollowingMap((prev) => {
        const nextMap = { ...(append ? prev : {}) };
        results.forEach((u) => {
          nextMap[u.user_id] = Boolean(u?.is_following);
        });
        return nextMap;
      });
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load list");
    } finally {
      append ? setLoadingMore(false) : setLoading(false);
    }
  };

  // Debounced querying
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => fetchList(1, false), 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    if (!open) return;
    // initial load
    fetchList(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Clear all data when modal closes
  useEffect(() => {
    if (!open) {
      // Clear all state data
      setList([]);
      setPage(1);
      setHasMore(false);
      setIsFollowingMap({});
      setRowLoading({});
      setQuery("");
      setLoadingMore(false);
    }
  }, [open]);

  // infinite scrolling via container onScroll
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 24;
      if (nearBottom && hasMore && !loadingMore && !loading) {
        fetchList(page + 1, true);
      }
    };
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loadingMore, loading, page]);

  const handleUserClick = (userId) => {
    navigate(`/gallery/${userId}`);
    onClose(); // Close the modal after navigation
  };

  const toggleFollow = async (user) => {
    const uid = user?.user_id;
    if (!uid) return;
    // If not logged in, open the same unauthenticated dialog as elsewhere
    if (!isUserLoggedIn()) {
      dispatch(
        openNonAuthenticatedModal({
          title: "Follow User?",
          action: "follow",
          type: "user",
          headerIcon: InfoOutlinedIcon,
          headerIconProps: {
            sx: { color: "#9B5AF8", width: "24px", height: "24px" },
          },
        })
      );
      return;
    }
    setRowLoading((m) => ({ ...m, [uid]: true }));
    const currentlyFollowing = Boolean(isFollowingMap[uid]);
    try {
      const path = currentlyFollowing
        ? API_PATH.UNFOLLOW_USER(uid)
        : API_PATH.FOLLOW_USER(uid);
      const res = await API.post(path);
      const message = res?.data?.message;
      if (message) toast.success(message);
      setIsFollowingMap((m) => ({ ...m, [uid]: !currentlyFollowing }));
      // Ask parent to refresh counts for the visible profile
      if (typeof onCountsRefresh === "function") {
        onCountsRefresh();
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Unable to update follow");
    } finally {
      setRowLoading((m) => ({ ...m, [uid]: false }));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 550,
          borderRadius: "16px",
          border: "1px solid #E1E1E1",
          overflow: "hidden",
        },
      }}
    >
      <Box sx={{ position: "relative", p: 2 }}>
        <Typography variant="h6" sx={{ textAlign: "center", fontWeight: 700 }}>
          {title}
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <Box sx={{ p: 2, pt: 1 }}>
        <TextField
          fullWidth
          placeholder="Search here"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#9CA3AF" }} />
              </InputAdornment>
            ),
          }}
          size="small"
        />
      </Box>
      <Box sx={{ px: 2, pb: 2 }}>
        {loading && page === 1 ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : list.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 6,
              px: 2,
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                backgroundColor: "#F3F4F6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <Typography sx={{ fontSize: 32, color: "#9CA3AF" }}>
                👤
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 600,
                color: "#111827",
                textAlign: "center",
                mb: 1,
              }}
            >
              No users found
            </Typography>
            <Typography
              sx={{
                fontSize: 14,
                color: "#6B7280",
                textAlign: "center",
                maxWidth: 280,
              }}
            >
              {query
                ? `No users match your search for "${query}". Try a different term.`
                : `No ${type} to display at the moment.`}
            </Typography>
          </Box>
        ) : (
          <List
            disablePadding
            ref={scrollRef}
            sx={{ maxHeight: "60vh", overflowY: "auto" }}
          >
            {list.map((u) => {
              const fullName = `${u.first_name || ""} ${
                u.last_name || ""
              }`.trim();
              const username = u.username || "";
              const isFollowing = Boolean(isFollowingMap[u.user_id]);
              const loggedInUserId = getLoggedInUser()?.id;
              const isCurrentUser = u.user_id === loggedInUserId;

              // Button text logic based on isUserProfile
              const buttonLabel = isFollowing
                ? !isUserProfile
                  ? "Following" // When viewing other user's profile and already following
                  : "Unfollow" // When viewing own profile
                : !isUserProfile
                ? "Follow" // When viewing other user's profile and not following
                : type === "followers"
                ? "Follow Back" // When viewing own profile and they follow you
                : "Follow"; // When viewing own profile and in following list
              return (
                <ListItem key={u.user_id} sx={{ py: 1.25 }} divider>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ width: "100%" }}
                  >
                    <Box
                      onClick={() => handleUserClick(u.user_id)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        cursor: "pointer",
                        "&:hover": {
                          opacity: 0.8,
                        },
                        maxWidth: "50%",
                      }}
                    >
                      <UserImage size={44} image={u.profile_image} />
                      <Box sx={{ ml: 1.5, minWidth: 0, flex: 1 }}>
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: "#111827",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {fullName || username}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: "#6B7280",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {username}
                        </Typography>
                      </Box>
                    </Box>
                    {/* Only show button if it's not the current logged-in user */}
                    {!isCurrentUser && (
                      <Box>
                        <Button
                          variant={isFollowing ? "outlined" : "contained"}
                          onClick={() => toggleFollow(u)}
                          disabled={rowLoading[u.user_id]}
                          sx={{
                            borderRadius: "4px",
                            textTransform: "none",
                            minWidth: 120,
                            px: 2,
                            py: 0.5,
                            fontSize: 13,
                            ...(isFollowing
                              ? {
                                  borderColor: "#9B5AF8",
                                  color: "#9B5AF8",
                                  backgroundColor: "#FFFFFF",
                                  "&:hover": {
                                    borderColor: "#7F43D1",
                                    backgroundColor: "#FFFFFF",
                                  },
                                }
                              : {
                                  borderColor: "#9B5AF8",
                                  backgroundColor: "#9B5AF8",
                                  color: "#FFFFFF",
                                  "&:hover": {
                                    backgroundColor: "#7F43D1",
                                    borderColor: "#7F43D1",
                                  },
                                }),
                          }}
                        >
                          {rowLoading[u.user_id] ? (
                            <CircularProgress size={16} />
                          ) : (
                            buttonLabel
                          )}
                        </Button>
                      </Box>
                    )}
                  </Stack>
                </ListItem>
              );
            })}
            {loadingMore && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            )}
          </List>
        )}
      </Box>
    </Dialog>
  );
};

export default FollowersFollowingModal;
